<template>
  <ion-page>
    <PageHeaderWithMenu title="Очередь конвертации" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Состояние</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p v-if="loading">Загрузка…</p>
          <p v-else-if="!snapshot?.available" class="warn-text">
            {{ snapshot?.message || 'Очередь недоступна' }}
          </p>
          <p v-else class="ok-text">RabbitMQ доступен. Показаны ожидающие задачи и видео медиатеки, которые уже конвертируются.</p>
          <ion-button expand="block" fill="outline" @click="loadQueue()" :disabled="loading">
            Обновить
          </ion-button>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Ожидают в очереди ({{ waiting.length }})</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-list v-if="waiting.length">
            <ion-item v-for="item in waiting" :key="item.itemKey" class="queue-row">
              <ion-label class="ion-text-wrap">
                <h2>{{ item.displayName }}</h2>
                <p>{{ taskTypeLabel(item.taskType) }}</p>
                <p v-if="item.sectionFolderKey || item.inputPath" class="path-line">
                  {{ item.sectionFolderKey || item.inputPath }}
                </p>
              </ion-label>
              <ion-button slot="end" fill="clear" color="danger" @click="confirmRemove(item)" :disabled="removingKey === item.itemKey">
                <ion-icon slot="icon-only" :icon="trashOutline"></ion-icon>
              </ion-button>
            </ion-item>
          </ion-list>
          <p v-else class="empty-hint">Очередь пуста.</p>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Сейчас конвертируются ({{ processing.length }})</ion-card-title>
          <p class="card-hint">Видео медиатеки в статусе PROCESSING, которых уже нет в очереди. Отмена остановит ffmpeg у воркера.</p>
        </ion-card-header>
        <ion-card-content>
          <ion-list v-if="processing.length">
            <ion-item v-for="item in processing" :key="item.itemKey" class="queue-row">
              <ion-label class="ion-text-wrap">
                <h2>{{ item.displayName }}</h2>
                <p>{{ taskTypeLabel(item.taskType) }} · id {{ item.videoId }}</p>
              </ion-label>
              <ion-button
                v-if="item.videoId"
                slot="end"
                fill="clear"
                color="warning"
                @click="confirmCancelProcessing(item)"
                :disabled="cancellingId === item.videoId"
              >
                <ion-icon slot="icon-only" :icon="stopCircleOutline"></ion-icon>
              </ion-button>
            </ion-item>
          </ion-list>
          <p v-else class="empty-hint">Нет активных конвертаций медиатеки вне очереди.</p>
        </ion-card-content>
      </ion-card>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { GlobalContext } from '@/model/globalcontext.model';
import AdminService, { IConversionQueueItem, IConversionQueueSnapshot } from '@/services/admin.service';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import {
  alertController,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  toastController,
} from '@ionic/vue';
import { stopCircleOutline, trashOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';

export default defineComponent({
  name: 'AdminConversionQueue',
  components: {
    PageHeaderWithMenu,
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return {
      isMobile: globalContext.isMobile,
      trashOutline,
      stopCircleOutline,
    };
  },
  data() {
    return {
      adminService: new AdminService(),
      headerScrolled: false,
      loading: false,
      snapshot: null as IConversionQueueSnapshot | null,
      removingKey: '' as string,
      cancellingId: null as number | null,
    };
  },
  computed: {
    waiting(): IConversionQueueItem[] {
      return this.snapshot?.waiting || [];
    },
    processing(): IConversionQueueItem[] {
      return this.snapshot?.processing || [];
    },
  },
  mounted() {
    void this.loadQueue();
  },
  methods: {
    onContentScroll(e: CustomEvent) {
      this.headerScrolled = (e.detail?.scrollTop ?? 0) > 16;
    },
    taskTypeLabel(taskType?: string): string {
      switch (taskType) {
        case 'education_video':
          return 'Видео медиатеки';
        case 'release_video':
          return 'Видео релиза';
        case 'release_music':
          return 'Музыка релиза';
        default:
          return taskType || 'Задача';
      }
    },
    async loadQueue() {
      this.loading = true;
      try {
        this.snapshot = await this.adminService.getConversionQueue();
      } catch (e) {
        console.error('loadQueue', e);
        this.snapshot = {
          available: false,
          message: 'Не удалось загрузить очередь',
          waiting: [],
          processing: [],
        };
      } finally {
        this.loading = false;
      }
    },
    async confirmRemove(item: IConversionQueueItem) {
      const alert = await alertController.create({
        header: 'Удалить из очереди?',
        message: `Убрать «${item.displayName}» из очереди ожидания?`,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: () => {
              void this.removeItem(item);
            },
          },
        ],
      });
      await alert.present();
    },
    async removeItem(item: IConversionQueueItem) {
      this.removingKey = item.itemKey;
      try {
        await this.adminService.removeConversionQueueItem(item.itemKey);
        const toast = await toastController.create({
          message: 'Задача удалена из очереди',
          duration: 2000,
          color: 'success',
        });
        await toast.present();
        await this.loadQueue();
      } catch (e) {
        console.error('removeItem', e);
        const toast = await toastController.create({
          message: 'Не удалось удалить задачу',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.removingKey = '';
      }
    },
    async confirmCancelProcessing(item: IConversionQueueItem) {
      if (!item.videoId) return;
      const alert = await alertController.create({
        header: 'Отменить конвертацию?',
        message: `Остановить конвертацию «${item.displayName}»?`,
        buttons: [
          { text: 'Нет', role: 'cancel' },
          {
            text: 'Отменить',
            role: 'destructive',
            handler: () => {
              void this.cancelProcessing(item.videoId!);
            },
          },
        ],
      });
      await alert.present();
    },
    async cancelProcessing(videoId: number) {
      this.cancellingId = videoId;
      try {
        await this.adminService.cancelConversion(videoId);
        const toast = await toastController.create({
          message: 'Конвертация отменена',
          duration: 2000,
          color: 'warning',
        });
        await toast.present();
        await this.loadQueue();
      } catch (e) {
        console.error('cancelProcessing', e);
        const toast = await toastController.create({
          message: 'Не удалось отменить конвертацию',
          duration: 3000,
          color: 'danger',
        });
        await toast.present();
      } finally {
        this.cancellingId = null;
      }
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  --background: var(--liga-float-glass-bg-strong);
}

.admin-card ion-card-title {
  color: var(--liga-chrome-text);
}

.admin-card ion-card-content,
.admin-card ion-item {
  --background: transparent;
  --color: var(--liga-chrome-text);
  color: var(--liga-chrome-text);
}

.admin-card ion-list {
  background: transparent;
}

.card-hint {
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: var(--liga-chrome-text-muted);
}

.warn-text {
  color: var(--ion-color-warning);
}

.ok-text {
  color: var(--liga-chrome-text-muted);
}

.empty-hint {
  color: var(--liga-chrome-text-muted);
}

.path-line {
  font-size: 0.8rem;
  word-break: break-all;
}

.queue-row ion-label h2 {
  color: var(--liga-chrome-text);
}

.queue-row ion-label p {
  color: var(--liga-chrome-text-muted);
}

.admin-card ion-button:not([fill='clear']) {
  --background: var(--ion-color-orange);
  --color: var(--liga-on-accent);
  --border-radius: 14px;
  font-weight: bold;
  margin-top: 8px;
}
</style>
